import { PlusOutlined } from '@ant-design/icons'
import { makeStyles } from '@material-ui/core'
import { Button, Input, notification, Select, Space } from 'antd'
import React, { useEffect, useState } from 'react'
import { trackPromise, usePromiseTracker } from 'react-promise-tracker'
import { useData } from '../../../data'
import { useRouter } from '../../../routes'
import DashboardLayout, { DashboardPage } from '../layout'
import AddNewJob from './new_job'
import { multiStepCallbacks } from './single/multistep_scheduler'
import JobsTableView from './table_view'

const useStyles = makeStyles(theme => ({
    content: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        height: '100%',
        paddingTop: theme.spacing(3)
    }
}))

/**
 * Jobs Dashboard page
 */
const Jobs = (props) => {
    const router = useRouter()
    const [currentPage, setCurrentPage] = useState(router.query?.page ? parseInt(router.query?.page) : 1)
    const [jobs, setJobs] = useState([])
    const [visible, setVisible] = useState(false)
    const [limit, setLimit] = useState(router.query?.limit ? parseInt(router.query?.limit) : 10)
    const [count, setCount] = useState(0)
    const [ordering, setOrdering] = useState(router.query?.ordering ?? '-id')
    const [searchBy, setSearchBy] = useState(router.query?.search ?? ',')
    const [filterBy, setFilterBy] = useState(router.query?.status ?? '')
    const [typeBy, setTypeBy] = useState(router.query?.type ?? '')

    const [userName, setUserName] = useState(router.query?.username ?? '')
    const [toolName, setToolName] = useState(router.query?.tool_name ?? '')
    const [toolId, setToolId] = useState(router.query?.tool_id ?? '')
    const [filterForm, setFilterForm] = useState({ 'search_input': "", "search_select": "" })
    const classes = useStyles()
    const data = useData()

    const getUsers = () => {
        trackPromise(
            data.users().getAllUsers()
                .then(users => {
                    const names = [...new Set(users.map(j => j.first_name))]
                    setUsernameFilter(names.map(n => ({ text: n, value: n })))
                }).catch(console.log)
        )
    }
    /**
     * Retrieve a list of all jobs from the data layer
     */

    const getTools = () => {
        trackPromise(
            data.tools().getAllTools()
                .then(tools => {
                    const tools_filter = [...new Set(tools.map(j => j.name))]
                    setToolFilters(tools_filter.map(n => ({ text: n, value: n })))
                }).catch(console.log)
        )
    }

    const [usersFilter, setUsernameFilter] = useState(() => {
        const initialState = getUsers()
        return initialState;
    })
    const [toolsFilter, setToolFilters] = useState(() => {
        const initialState = getTools()
        return initialState;
    })

    /**
     * Retrieve a list of all jobs from the data layer
     */
    const getJobs = () => {
        const jobs = data.jobs()
        const offset = (currentPage - 1) * limit;

        trackPromise(
            jobs.getAllJobs(limit, offset, ordering, searchBy, filterBy, typeBy, toolId)
                .then(async res => {
                    const { jobs, count } = res
                    setJobs(jobs)
                    setCount(count)

                    router.replace({
                        search: `?page=${currentPage}&limit=${limit}&ordering=${ordering}&type=${typeBy}&tool=${toolId}&status=${filterBy}&search=${searchBy}`
                    })
                })
        )
    }
    useEffect(() => {
        getJobs()

        // eslint-disable-next-line
    }, [limit, currentPage, ordering, searchBy, filterBy, typeBy, toolId])

    /**
     * Create a new job with no template
     */
    const newJob = () => {
        setVisible(true)
    }

    // close drawer
    const close = (job) => {
        setVisible(false)

        const { id } = job
        if (id) {
            // reload the jobs
            router.push(`/dashboard/job?id=${id}`)
        }
    }

    // open details
    const selectJob = (job) => {
        const { id } = job

        router.push(`/dashboard/job?id=${id}`)
    }

    /**
     * schedule a job to run
     */
    const scheduleJob = (job) => {
        const { id } = job
        const jobs = data.jobs()

        trackPromise(
            jobs.scheduleRunJob(id)
                .then(() => {
                    getJobs()
                }).catch(err => {
                    notification.error({
                        message: "Error running job",
                        description: "An error occured scheduling a job. Check console for logs"
                    })
                })
        )
    }

    // delete a job
    const deleteJob = (job) => {
        const { id } = job
        const jobs = data.jobs()

        trackPromise(
            jobs.deleteJob(id)
                .then(() => {
                    getJobs()
                }).catch(err => {
                    notification.error({
                        message: "Error running job",
                        description: "An error occured scheduling a job. Check console for logs"
                    })

                    console.log(err)
                })
        )
    }

    // retry a job
    const retryJob = (job) => {
        const jobs = data.jobs();
        multiStepCallbacks[job.tool_category][job.stage - 1](job, jobs, getJobs);
    }

    // stop a job
    const stopJob = (job) => {
        const { id: jobId } = job;
        const jobs = data.jobs();

        if (jobId) {
            trackPromise(
                jobs.cancelJob(jobId)
                    .then(() => getJobs())
                    .catch(err => {
                        console.log(err)
                        notification.error({
                            message: "Error running job",
                            description: "There was an error scheduling your job. Check console for details."
                        })
                    }), 'cancel_job'
            )
        }
    }

    // show tool details
    const showDetails = (id) => {
        const tool_id = id
        router.push(`/dashboard/tool?id=${tool_id}`)
    }

    // loading data
    const { promiseInProgress } = usePromiseTracker()

    // search by filter
    const searchFor = (value, event) => {
        let current_search = searchBy.split(',')
        if (["PENDING", "PROCESSING", "DONE", "ERROR", "PAUSED", "TERMINATED"].includes(value)) {
            current_search[1] = value
            setSearchBy(current_search.join(","))
        }
        else {
            current_search[0] = value
            setSearchBy(current_search.join(","))
        }
    }
    const filterFor = (value, event) => {
        setFilterBy(value)
    }
    const clearFilter = () => {
        setFilterForm({ 'search_input': "", "search_select": "" })
        setSearchBy("")
        setFilterBy("")
    }

    const handleFormChange = e => {
        const { name, value } = e.target
        setFilterForm(data => ({ ...data, [name]: value }))
    }

    const handleFormSelectChange = value => {
        handleFormChange({ target: { name: "search_select", value: value } })
        // searchFor(value)
        filterFor(value)
    }


    return (
        <DashboardLayout
            selected="jobs"
        >
            <DashboardPage
                title="Jobs"
                buttonText="Create a Job"
                onCtaClick={newJob}
                ctaIcon={<PlusOutlined />}
            >
                <Space direction="horizontal">
                    <Input.Search
                        placeholder="Search"
                        onSearch={searchFor}
                        name="search_input"
                        value={filterForm.search_input}
                        onChange={handleFormChange}
                        enterButton="search"
                    />
                    <Select placeholder="Filter by" style={{ 'width': "200px" }} name="search_select" onChange={handleFormSelectChange} value={filterForm.search_select}>
                        <Select.Option value="PENDING">PENDING</Select.Option>
                        <Select.Option value="PROCESSING">PROCESSING</Select.Option>
                        <Select.Option value="DONE">DONE</Select.Option>
                        <Select.Option value="ERROR">ERROR</Select.Option>
                        <Select.Option value="PAUSED">PAUSED</Select.Option>
                        <Select.Option value="TERMINATED">TERMINATED</Select.Option>
                    </Select>
                    <Button onClick={() => clearFilter()}>Clear Filter</Button>
                </Space>

                <div className={classes.content}>
                    <JobsTableView
                        deleteJob={deleteJob}
                        jobs={jobs}
                        loading={promiseInProgress}
                        retryJob={retryJob}
                        selectJob={selectJob}
                        scheduleJob={scheduleJob}
                        stopJob={stopJob}
                        count={count}
                        updateLimit={setLimit}
                        page={currentPage}
                        limit={limit}
                        setOrdering={setOrdering}
                        updatePage={setCurrentPage}
                        setUserName={setUserName}
                        setToolName={setToolName}
                        usersFilter={usersFilter}
                        allTools={toolsFilter}
                        setFilterBy={setFilterBy}
                        setTypeBy={setTypeBy}
                        setToolId={setToolId}
                        onSelectTool={showDetails}
                    />
                </div>

                <AddNewJob
                    visible={visible}
                    close={close}
                />
            </DashboardPage>
        </DashboardLayout>
    )

}

export default Jobs
